/**
 * LongPollTransport — long-polling fallback for Parlant events.
 *
 * Mirrors the logic that lived in useEventPolling, but as a framework-free
 * strategy so it is testable and swappable without React.
 *
 * Adaptive intervals:
 *   - ACTIVE  (50 ms)  — bot is processing or typing
 *   - NORMAL (1 000 ms) — recent activity
 *   - IDLE   (3 000 ms) — 10 s after last message
 *   - VERY_IDLE (5 000 ms) — 30 s after last message
 */

import { BaseTransport } from './ChatTransport';
import { BackoffPolicy } from './BackoffPolicy';
import type { StatusType } from '../types/parlant';
import { normalizeParlantEvents } from '../utils/parlantEvent';

export interface LongPollOptions {
  serverUrl: string;
  authToken?: string;
  /** Seconds to pass as wait_for_data (default 30). */
  waitForData?: number;
}

const ACTIVE_MS = 50;
const NORMAL_MS = 1_000;
const IDLE_MS = 3_000;
const VERY_IDLE_MS = 5_000;
const IDLE_THRESHOLD_MS = 10_000;
const VERY_IDLE_THRESHOLD_MS = 30_000;

export class LongPollTransport extends BaseTransport {
  private readonly serverUrl: string;
  private readonly authToken?: string;
  private readonly waitForData: number;

  private sessionId: string | null = null;
  private lastOffset = 0;

  private running = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private abortController: AbortController | null = null;
  private backoff: BackoffPolicy;

  private botStatus: StatusType = 'ready';
  private lastUserMessageAt = 0;
  private lastEventAt = 0;

  constructor(opts: LongPollOptions) {
    super();
    this.serverUrl = opts.serverUrl.endsWith('/')
      ? opts.serverUrl.slice(0, -1)
      : opts.serverUrl;
    this.authToken = opts.authToken;
    this.waitForData = opts.waitForData ?? 30;
    this.backoff = new BackoffPolicy({ baseMs: 1_000, maxMs: 30_000 });
  }

  override start(sessionId: string, lastOffset: number): void {
    this.stop();
    this.running = true;
    this.sessionId = sessionId;
    this.lastOffset = lastOffset;
    this.backoff.reset();
    this._scheduleNext(0);
  }

  override stop(): void {
    this.running = false;
    this._clearTimer();
    this.abortController?.abort();
    this.abortController = null;
    this.backoff.destroy();
  }

  /** Call from the React layer when the user sends a message. */
  notifyUserMessage(): void {
    this.lastUserMessageAt = Date.now();
  }

  /** Update the perceived bot status (used for interval calculation). */
  updateBotStatus(status: StatusType): void {
    this.botStatus = status;
  }

  // -------------------------------------------------------------------------

  private _scheduleNext(delayMs: number): void {
    this._clearTimer();
    this.timer = setTimeout(() => {
      this.timer = null;
      if (this.running) void this._poll();
    }, delayMs);
  }

  private _clearTimer(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private async _poll(): Promise<void> {
    if (!this.running || !this.sessionId) return;

    this.abortController?.abort();
    this.abortController = new AbortController();
    const timeoutMs = (this.waitForData + 10) * 1_000;

    const url =
      `${this.serverUrl}/sessions/${this.sessionId}/events` +
      `?min_offset=${this.lastOffset}&wait_for_data=${this.waitForData}`;

    const headers: Record<string, string> = {};
    if (this.authToken) headers['Authorization'] = `Bearer ${this.authToken}`;

    const timeoutId = setTimeout(() => this.abortController?.abort(), timeoutMs);

    try {
      const response = await fetch(url, {
        headers,
        signal: this.abortController.signal,
        cache: 'no-store',
      });

      clearTimeout(timeoutId);

      if (response.status === 404) {
        this.emit('error', 'Session not found (404) — stopping polling');
        this.running = false;
        return;
      }

      if (response.status === 504) {
        // Server-side timeout — normal, just poll again immediately
        this.backoff.reset();
        this._scheduleNext(this._interval());
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const raw = (await response.json()) as Record<string, unknown>[];
      const events = normalizeParlantEvents(raw);

      if (events && events.length > 0) {
        const maxOffset = Math.max(...events.map(e => e.offset));
        this.lastOffset = maxOffset + 1;
        this.lastEventAt = Date.now();

        // Derive bot status from status events
        const lastStatus = [...events]
          .reverse()
          .find(e => e.kind === 'status');
        if (lastStatus) {
          const s = (lastStatus.data as Record<string, unknown>)?.status as StatusType | undefined;
          if (s) this.botStatus = s;
        }

        this.emit('events', events);
      }

      this.backoff.reset();
      this._scheduleNext(this._interval());
    } catch (err) {
      clearTimeout(timeoutId);
      if (!this.running) return;

      if (err instanceof Error && err.name === 'AbortError') {
        this._scheduleNext(this._interval());
        return;
      }

      const delay = this.backoff.next();
      this.emit('error', `Long-poll error, retrying in ${Math.round(delay)}ms: ${String(err)}`);
      this._scheduleNext(delay);
    }
  }

  private _interval(): number {
    const now = Date.now();
    if (this.botStatus === 'processing' || this.botStatus === 'typing') return ACTIVE_MS;
    if (now - this.lastUserMessageAt < 5_000 || now - this.lastEventAt < 5_000) return ACTIVE_MS;
    if (now - this.lastUserMessageAt < IDLE_THRESHOLD_MS) return NORMAL_MS;
    if (now - this.lastUserMessageAt < VERY_IDLE_THRESHOLD_MS) return IDLE_MS;
    return VERY_IDLE_MS;
  }
}
